import { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom'
import {
	Box,
	Button,
	Container,
	Divider,
	Drawer,
	IconButton,
	List,
	ListItemButton,
	ListItemText,
	Tooltip,
	Typography,
	useTheme,
	useMediaQuery,
	CircularProgress,
	InputBase,
} from '@mui/material'
import {
	ArrowBack,
	ArrowForward,
	ChevronRight,
	Close,
	ExpandLess,
	ExpandMore,
	InfoOutlined,
	YouTube,
} from '@mui/icons-material'
import { doGet, doStreamPost }                                                         from '../../util/do-fetch'
import { createQueuedUserMessage, createStreamingAiMessage, PREPKIT_QUESTION_ACTIONS } from '../../util/prepkit-utils'
import Messages           from '../prepkit/chat/Messages'
import SendMessage        from '../prepkit/chat/SendMessage'
import QuestionActions from './QuestionActions'

const OPTION_LETTERS = ['A', 'B', 'C', 'D']

const getOrCreateAnonId = () => {
	const key = 'anon_id'
	let id = sessionStorage.getItem(key)
	if (!id) {
		id = crypto.randomUUID()
		sessionStorage.setItem(key, id)
	}
	return id
}

// Defined outside component to avoid recreation on every render
const QuestionCard = ({
	question,
	theme,
	isSmallScreen,
	selectedAnswer,
	expanded,
	collapsible,
	onOptionClick,
	onInfoClick,
	onExpandToggle,
}) => {
	const getOptionLetterSx = (i) => {
		const base = {
			display: 'inline-flex',
			alignItems: 'center',
			justifyContent: 'center',
			width: isSmallScreen ? '22px' : '34px',
			height: isSmallScreen ? '22px' : '34px',
			minWidth: isSmallScreen ? '22px' : '34px',
			borderRadius: '50%',
			fontSize: isSmallScreen ? '13px' : '16px',
			fontWeight: '500',
			border: `1px solid ${theme.palette.primary.main}`,
			color: theme.palette.text.primary,
			flexShrink: 0,
			mt: '3px',
			transition: 'background 250ms, borderColor 250ms, color 250ms',
		}
		if (selectedAnswer !== null) {
			if (i === question.answer) return { ...base, borderColor: theme.palette.success.main, backgroundColor: theme.palette.success.main, color: theme.palette.success.contrastText }
			if (i === selectedAnswer)  return { ...base, borderColor: theme.palette.error.main,   backgroundColor: theme.palette.error.main,   color: theme.palette.error.contrastText   }
		}
		return base
	}

	const getOptionLabelSx = (i) => {
		const base = {
			flex: 1,
			fontSize: isSmallScreen ? '16px' : '18px',
			ml: isSmallScreen ? '10px' : '15px',
			pt: isSmallScreen ? '3px' : '7px',
			pb: isSmallScreen ? 0 : '5px',
			transition: 'color 250ms',
		}
		if (selectedAnswer !== null) {
			if (i === question.answer) return { ...base, color: theme.palette.success.main }
			if (i === selectedAnswer)  return { ...base, color: theme.palette.error.main  }
		}
		return base
	}

	return (
		<Box
			sx={{
				backgroundColor: theme.palette.background.paper,
				px: isSmallScreen ? 2 : 4,
				p: 4,
				borderRadius: isSmallScreen ? 0 : 3,
				border: collapsible
					? `4px solid ${theme.palette.background.paper}`
					: `1px solid ${theme.palette.divider}`,
				borderLeftWidth: isSmallScreen ? 0 : undefined,
				borderRightWidth: isSmallScreen ? 0 : undefined,
				boxShadow: !collapsible && !isSmallScreen ? theme.shadows[2] : 'none',
			}}
		>
			{/* Question text + More/Less toggle */}
			<Box
				display="flex"
				flexDirection="row"
				justifyContent="space-between"
				alignItems="flex-start"
				sx={{ cursor: collapsible ? 'pointer' : 'default', mb: 1 }}
				onClick={() => collapsible && onExpandToggle()}
			>
				<Typography sx={{ fontSize: isSmallScreen ? '18px' : '22px', lineHeight: isSmallScreen ? '24px' : '30px', flex: 1 }}>
					{question.question}
				</Typography>
				{collapsible && (
					<Button size="small" sx={{ flexShrink: 0, ml: 1 }}>
						{expanded ? 'Less' : 'More'}
						{expanded ? <ExpandLess /> : <ExpandMore />}
					</Button>
				)}
			</Box>

			{/* Options — hidden when pinned+collapsed */}
			{(!collapsible || expanded) && (
				<Box>
					{question.options.map((opt, i) => (
						<Box
							key={i}
							display="flex"
							alignItems="flex-start"
							sx={{
								py: isSmallScreen ? '2px' : '2px',
								px: isSmallScreen ? '3px' : '5px',
								my: 2,
								borderRadius: isSmallScreen ? '12px' : '30px',
								cursor: selectedAnswer !== null ? 'default' : 'pointer',
								transition: 'background 150ms',
								'&:hover': selectedAnswer !== null ? {} : { backgroundColor: theme.palette.action.selected },
							}}
							onClick={() => onOptionClick(i)}
						>
							<Box sx={getOptionLetterSx(i)}>
								{OPTION_LETTERS[i]}
							</Box>
							<Box sx={getOptionLabelSx(i)}>
								{opt.label}
							</Box>
							<Tooltip placement="right" title={`Tell me about "${opt.label}"`} enterDelay={300}>
								<IconButton
									size="small"
									sx={{ ml: 0.5, mt: '1px', flexShrink: 0, opacity: 0.35, '&:hover': { opacity: 1 } }}
									onClick={(e) => { e.stopPropagation(); onInfoClick(opt.label) }}
								>
									<InfoOutlined  />
								</IconButton>
							</Tooltip>
						</Box>
					))}
				</Box>
			)}
		</Box>
	)
}

function AnonQuestionView() {
	const theme         = useTheme()
	const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'))
	const { code }      = useParams()
	const navigate      = useNavigate()
	const anonId        = getOrCreateAnonId()

	const [question, setQuestion]               = useState(null)
	const [trialPackages, setTrialPackages]     = useState([])
	const [category, setCategory]               = useState(null)
	const [categoryPackages, setCategoryPackages] = useState([])
	const [drawerOpen, setDrawerOpen]           = useState(false)
	const [loading, setLoading]                 = useState(true)
	const [messages, setMessages]               = useState([])
	const [sending, setSending]                 = useState(false)
	const [selectedAnswer, setSelectedAnswer]   = useState(null)
	const [isPinned, setIsPinned]               = useState(false)
	const [expanded, setExpanded]               = useState(false)
	const [showActions, setShowActions]         = useState(false)
	const [ctaVisible, setCtaVisible]           = useState(false)
	const [codeInput, setCodeInput]             = useState(code || '')

	const messagesEndRef  = useRef(null)
	const questionRef     = useRef(null)
	const prevSendingRef  = useRef(false)
	const ctaTimerRef     = useRef(null)
	const ctaRef          = useRef(null)

	// Load question on code change
	useEffect(() => {
		if (!code) return
		setLoading(true)
		setQuestion(null)
		setMessages([])
		setSelectedAnswer(null)
		setIsPinned(false)
		setExpanded(false)

		doGet(`/demo/question/${code}`)
			.then(async res => {
				if (res.status === 404) {
					navigate(`/q?err=${encodeURIComponent(code)}`, { replace: true })
					return
				}
				if (!res.ok) throw new Error('Failed to load question')
				const data = await res.json()
				setQuestion(data.question)
				setTrialPackages(data.trialPackages || [])
				setCategory(data.category || null)
				setCategoryPackages(data.categoryPackages || [])
			})
			.catch(err => {
				console.error(err)
				navigate(`/q?err=${encodeURIComponent(code)}`, { replace: true })
			})
			.finally(() => setLoading(false))
	}, [code])

	// Scroll to bottom on new messages
	useEffect(() => {
		if (messagesEndRef.current) {
			messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
		}
	}, [messages])

	// Hide CTA when sending; fade back in 3s after AI finishes
	useEffect(() => {
		if (sending) {
			clearTimeout(ctaTimerRef.current)
			setCtaVisible(false)
		} else if (prevSendingRef.current === true) {
			// sending just finished — start 3s delay before fading in
			ctaTimerRef.current = setTimeout(() => setCtaVisible(true), 3000)
		}
		prevSendingRef.current = sending
		return () => clearTimeout(ctaTimerRef.current)
	}, [sending])

	// Scroll CTA into view when it becomes visible, accounting for fixed input bar
	useEffect(() => {
		if (ctaVisible && ctaRef.current) {
			setTimeout(() => {
				const el = ctaRef.current
				if (!el) return
				const rect = el.getBoundingClientRect()
				const inputBarHeight = 120 // fixed input bar + padding clearance
				const overlap = rect.bottom - (window.innerHeight - inputBarHeight)
				if (overlap > 0) {
					window.scrollBy({ top: overlap, behavior: 'smooth' })
				}
			}, 50)
		}
	}, [ctaVisible])

	// Detect when question card scrolls off the top
	useEffect(() => {
		const handleScroll = () => {
			if (questionRef.current) {
				const rect = questionRef.current.getBoundingClientRect()
				setIsPinned(rect.bottom < 0)
			}
		}
		window.addEventListener('scroll', handleScroll, { passive: true })
		return () => window.removeEventListener('scroll', handleScroll)
	}, [])

	const handleSend = useCallback(async (text) => {
		if (!text || !text.trim() || sending) return
		setSending(true)
		setShowActions(false)

		const userMsg = createQueuedUserMessage(text.trim(), true)
		const aiMsg   = createStreamingAiMessage(true)
		aiMsg.streaming = true

		setMessages(prev => [...prev, userMsg, aiMsg])

		const history = [...messages, userMsg]

		doStreamPost(
			`/demo/question/${code}/${anonId}/send`,
			{ body: JSON.stringify({ messages: history }) },
			null,
			(chunk) => {
				setMessages(prev => {
					const idx = prev.findIndex(m => m.id === aiMsg.id)
					if (idx === -1) return prev
					const updated = [...prev]
					updated[idx] = { ...updated[idx], body: updated[idx].body + chunk, streaming: true }
					return updated
				})
			},
			(full) => {
				setMessages(prev => {
					const idx = prev.findIndex(m => m.id === aiMsg.id)
					if (idx === -1) return prev
					const updated = [...prev]
					updated[idx] = { ...updated[idx], body: full, streaming: false }
					return updated
				})
				setSending(false)
			}
		).catch(err => {
			console.error('Chat error', err)
			setSending(false)
		})
	}, [code, anonId, messages, sending])

	const handleCodeSubmit = (e) => {
		e.preventDefault()
		const trimmed = codeInput.trim().toUpperCase()
		if (trimmed) navigate(`/q/${trimmed}`)
	}

	const handleOptionClick = (index) => {
		if (selectedAnswer !== null) return
		setSelectedAnswer(index)
		handleSend(`${OPTION_LETTERS[index]}. ${question.options[index]?.label}`)
	}

	if (loading) {
		return (
			<Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
				<CircularProgress />
			</Box>
		)
	}

	if (!question) return null

	// CTA logic
	const singlePackage = trialPackages?.length === 1 ? trialPackages[0] : null
	const ctaLabel = singlePackage
		? `Try ${singlePackage.name} Free`
		: `Try Birdsy ${category?.name ? category.name + ' ' : ''}Exam Prep Free`
	const ctaUrl = singlePackage
		? `/try/${singlePackage.category?.token || category?.token}/${singlePackage.token}`
		: null

	const handleCtaClick = () => {
		if (ctaUrl) navigate(ctaUrl)
		else setDrawerOpen(true)
	}

	const sharedCardProps = {
		question,
		theme,
		isSmallScreen,
		selectedAnswer,
		onOptionClick: handleOptionClick,
		onInfoClick:   (label) => handleSend(`Tell me more about "${label}"`),
		onExpandToggle: () => setExpanded(e => !e),
	}

	return (
		<>
		<Container style={{ maxWidth: '800px', padding: '0 16px' }}>
			<Box py={3} pb="120px">

				{/* Topic name */}
				<Typography
					variant="body2"
					color="secondary"
					sx={{ mb: 1, px: isSmallScreen ? 0.5 : 0 }}
				>
					{question.topic?.name}
				</Typography>

				{/* Question card */}
				<Box ref={questionRef}>
					<QuestionCard {...sharedCardProps} collapsible={false} expanded={true} />
				</Box>

				

				{/* Canned prompts — shown when no messages; accessible via + otherwise */}
				{messages.length === 0 && (
					<Box
						display="flex"
						flexDirection={isSmallScreen ? 'column' : 'row'}
						justifyContent="center"
						alignItems={isSmallScreen ? 'flex-start' : 'center'}
						mt={4}
					>
						{PREPKIT_QUESTION_ACTIONS.map(a => (
							<Button
								key={a.label}
								onClick={() => handleSend(a.prompt)}
								sx={{
									textTransform: 'none',
									fontSize: '.9em',
									color: theme.palette.text.disabled,
									'&:hover': { color: theme.palette.secondary.main },
									mx: isSmallScreen ? 0 : 0.5,
								}}
							>
								{a.label}
							</Button>
						))}
					</Box>
				)}

				{/* Chat messages */}
				{messages.length > 0 && (
					<Box mt={3} mb={2}>
						<Messages messages={messages} contextId={anonId} showMessagesAfter={0} />
						<div ref={messagesEndRef} />
					</Box>
				)}

				{/* Session CTA — compact card below messages */}
				{selectedAnswer !== null && ctaVisible && (
					<Box display="flex" justifyContent="center" mt={2} ref={ctaRef}>
						<Box
							sx={{
								backgroundColor: theme.palette.background.default,
								borderRadius: 3,
								p: 2,
								textAlign: 'center',
								width: 300,
								animation: 'ctaFadeIn 0.5s ease',
								'@keyframes ctaFadeIn': {
									from: { opacity: 0, transform: 'translateY(6px)' },
									to:   { opacity: 1, transform: 'translateY(0)' },
								},
							}}
						>
							<Typography variant="subtitle1" fontWeight="bold" gutterBottom>
								How'd you do?
							</Typography>
							<Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
								Keep going with a full 30-minute Birdsy study session — FREE.
							</Typography>
							<Button
								variant="contained"
								size="medium"
								endIcon={<ArrowForward />}
								onClick={handleCtaClick}
								sx={{
									borderRadius: 2,
									px: 3,
									fontWeight: 'bold',
									textTransform: 'none',
								}}
							>
								Start My Free Session
							</Button>
							<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
								Free. No subscription.
							</Typography>
						</Box>
					</Box>
				)}

			</Box>
		</Container>

		{/* Pinned collapsed question card — shown when scrolled past the card */}
		{isPinned && (
			<Box
				sx={{
					position: 'fixed',
					top: 0,
					left: 0,
					right: 0,
					zIndex: 1300,
					borderBottom: `1px solid ${theme.palette.divider}`,
					boxShadow: '0px 3px 10px rgba(0,0,0,0.2)',
					backgroundColor: theme.palette.background.paper,
				}}
			>
				<Box sx={{ maxWidth: '800px', mx: 'auto', px: 2 }}>
					<QuestionCard {...sharedCardProps} collapsible={true} expanded={expanded} />
				</Box>
			</Box>
		)}

		{/* Fixed bottom input bar */}
		<Box
			sx={{
				position: 'fixed',
				bottom: 0,
				left: 0,
				right: 0,
				zIndex: 1200,
				backgroundColor: isSmallScreen ? theme.palette.background.paper : 'transparent',
			}}
		>
			<QuestionActions
				actions={PREPKIT_QUESTION_ACTIONS}
				onAction={(a) => handleSend(a.prompt)}
				show={showActions}
			/>
			<Box
				sx={{
					py: isSmallScreen ? 1 : 2,
					width: isSmallScreen ? 'auto' : '800px',
					mx: 'auto',
				}}
				display="flex"
				justifyContent="center"
				alignItems="center"
			>
				<SendMessage
					onSend={handleSend}
					placeholder="Ask Birdsy about this question…"
					actionsShown={showActions}
					onClickShowActions={() => setShowActions(s => !s)}
				/>
			</Box>
		</Box>

		{/* Package picker drawer */}
		<Drawer
			anchor="right"
			open={drawerOpen}
			onClose={() => setDrawerOpen(false)}
			PaperProps={{ sx: { width: { xs: '100%', sm: 420 } } }}
		>
			<Box
				display="flex"
				alignItems="center"
				px={2}
				py={1.5}
				sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}
			>
				<IconButton edge="start" onClick={() => setDrawerOpen(false)} sx={{ mr: 1 }}>
					<ArrowBack />
				</IconButton>
				<Typography variant="h6" fontWeight="bold" flex={1}>
					{category?.name}
				</Typography>
				<IconButton edge="end" onClick={() => setDrawerOpen(false)}>
					<Close />
				</IconButton>
			</Box>
			<List disablePadding>
				{categoryPackages.map((pkg) => (
					<Box key={pkg.id}>
						<ListItemButton
							onClick={() => {
								setDrawerOpen(false)
								navigate(`/try/${category.token}/${pkg.token}`)
							}}
							sx={{ py: 2, px: 2.5 }}
						>
							<ListItemText
								primary={pkg.name}
								primaryTypographyProps={{ fontWeight: 'bold' }}
							/>
							<ChevronRight sx={{ color: theme.palette.text.secondary }} />
						</ListItemButton>
						<Divider />
					</Box>
				))}
			</List>
		</Drawer>

		{/* Fixed top-center — YouTube question code entry */}
		{!isPinned && (
			<Box
				sx={{
					position: 'fixed',
					top: 12,
					left: '50%',
					transform: 'translateX(-50%)',
					zIndex: 1300,
				}}
			>
				<Box
					component="form"
					onSubmit={handleCodeSubmit}
					sx={{
						display: 'flex',
						alignItems: 'center',
						backgroundColor: theme.palette.background.paper,
						border: `1px solid ${theme.palette.divider}`,
						borderRadius: 3,
						pl: 1.5,
						pr: 0.5,
						py: 0.5,
						gap: 1,
					}}
				>
					<YouTube sx={{ color: 'red', fontSize: '1.2rem', flexShrink: 0 }} />
					<InputBase
						value={codeInput}
						onChange={(e) => setCodeInput(e.target.value)}
						placeholder="YouTube Question Code"
						inputProps={{
							style: {
								fontSize: '0.875rem',
								width: '220px',
							},
						}}
					/>
					<IconButton
						type="submit"
						size="small"
						disabled={!codeInput.trim()}
						sx={{ p: 0.5 }}
					>
						<ArrowForward fontSize="small" />
					</IconButton>
				</Box>
			</Box>
		)}
		</>
	)
}

export default AnonQuestionView
